import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/machine-locations", label: "Machine Locations" },
  { to: "/find-distributor", label: "Find Distributor" },
  { to: "/locate-us", label: "Locate Us" },
  { to: "/gallery", label: "Gallery" },
];

export default function Header() {
  const { settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const linkCls = ({ isActive }) =>
    `relative text-sm font-600 transition-colors duration-200 ${
      isActive ? "text-brand" : "text-slate-700 hover:text-brand"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-card" : "bg-white/80 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="container-page flex items-center justify-between py-3.5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand font-display text-lg font-800 text-white">A</span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg font-800 tracking-tight text-brand-navy">AIMWAY</span>
            <span className="text-[10px] font-500 uppercase tracking-[0.15em] text-brand-orange">Technology Solutions</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 xl:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${(settings.phone || "").replace(/\s+/g, "")}`}
            className="hidden items-center gap-2 text-sm font-600 text-brand-navy lg:flex"
            data-testid="header-phone"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-sky text-brand"><Phone size={15} /></span>
            {settings.phone}
          </a>
          <Link
            to="/contact"
            className="hidden rounded-full bg-brand-orange px-5 py-2.5 text-sm font-700 text-white transition-all duration-200 hover:bg-brand-orange-dark hover:shadow-soft sm:block"
            data-testid="header-enquire-btn"
          >
            Enquire Now
          </Link>
          <button className="xl:hidden text-brand-navy" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-100 bg-white xl:hidden"
          >
            <div className="container-page flex flex-col py-3">
              {[...links, { to: "/contact", label: "Contact Us" }].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="border-b border-slate-50 py-3 text-sm font-600 text-slate-700"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
